/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import React from 'react'
import PropTypes from 'prop-types'
import Helmet from 'react-helmet'
import {graphql, useStaticQuery} from 'gatsby'
import asLocaleField from '../helpers/asLocaleField'
import {useI18next} from 'gatsby-plugin-react-i18next'
import joinUrl from '../helpers/joinUrl'

const languages = ['pl', 'de', 'en']
const defaultLanguage = 'pl'

function SEO(props) {
    const {i18n, originalPath, language} = useI18next()
    const lang = i18n.language

    const {site, defaultFacebookImage, logoImage} = useStaticQuery(graphql`
        {
            site {
                siteMetadata {
                    siteUrl
                    title_pl
                    title_en
                    description_pl
                    description_en
                    author
                }
            }
            defaultFacebookImage: file(relativePath: {eq: "public_resources/thulium_cover.png"}) {
                publicURL
            }
            logoImage: file(relativePath: {eq: "images/logo.png"}) {
                publicURL
            }
        }
    `)
    const siteUrl = site.siteMetadata.siteUrl

    const {title, description, imagePath, noindex, jsonLD, redirectUrl} = props
    const metaTitle = title || asLocaleField(site.siteMetadata, 'title', lang)
    const metaDescription = description || asLocaleField(site.siteMetadata, 'description', lang)
    const facebookLocale = lang === 'en' ? 'en_US' : 'pl_PL'
    const facebookImagePath = `${siteUrl}${imagePath || defaultFacebookImage.publicURL}`

    const meta = [
        {
            name: `description`,
            content: metaDescription,
        },
        {
            property: `og:title`,
            content: metaTitle,
        },
        {
            property: `og:description`,
            content: metaDescription,
        },
        {
            property: `og:type`,
            content: `website`,
        },
        {
            property: `og:site_name`,
            content: `Thulium`,
        },
        {
            property: `og:locale`,
            content: facebookLocale,
        },
        {
            property: `og:image`,
            content: facebookImagePath,
        },
        {
            name: `twitter:card`,
            content: `summary_large_image`,
        },
        {
            name: `twitter:creator`,
            content: site.siteMetadata.author,
        },
        {
            name: `twitter:title`,
            content: metaTitle,
        },
        {
            name: `twitter:description`,
            content: metaDescription,
        },
    ]

    //TODO Remove en override when en version will be ready for indexing
    if (noindex || lang === 'en') {
        meta.push({name: `robots`, content: `noindex`})
    }

    const links = [
        {
            rel: 'canonical',
            href: `${siteUrl}${joinUrl(language, originalPath)}`,
        },
        {
            rel: 'preconnect',
            href: 'https://www.googletagmanager.com',
        },
        {
            rel: 'preconnect',
            href: 'https://www.google-analytics.com',
        },
        {
            rel: 'preconnect',
            href: 'https://consent.cookiebot.com',
        },
        {
            rel: 'preconnect',
            href: 'https://connect.facebook.net',
        },
        {
            rel: 'preconnect',
            href: 'https://cdn.thulium.com',
        },
        {
            rel: 'alternate',
            hrefLang: 'x-default',
            href: getPathWithLanguage(siteUrl, originalPath, defaultLanguage),
        },
    ]

    languages.forEach((lang) => {
        links.push({
            rel: 'alternate',
            hrefLang: lang,
            href: getPathWithLanguage(siteUrl, originalPath, lang),
        })
    })

    const scripts = [
        {
            type: 'application/ld+json',
            innerHTML: `{
                  "@context": "https://schema.org",
                  "@type": "Organization",
                  "url": "${siteUrl}",
                  "name": "Thulium sp. z o.o.",
                  "logo": "${siteUrl}${logoImage.publicURL}",
                  "sameAs": [
                    "https://facebook.com/thulium",
                    "https://linkedin.com/company/thulium",
                    "https://www.youtube.com/channel/UC4JAdgU_tCgOK3NT2lDqF8Q"
                  ],
                  "contactPoint": [{
                    "@type": "ContactPoint",
                    "telephone": "+48 12 397 53 00",
                    "email": "hello@thulium.com",
                    "contactType": "customer service",
                    "availableLanguage" : ["English", "Deutsch", "Polish"]
                  }],
                  "description": "Thulium is the only tool you need to support your customers quickly and efficiently. We provide SaaS platform for customer support via multiple channels of communication: e-mail, phone, chat, Facebook Messenger and SMS."
                }`,
        },
    ]

    if (jsonLD) {
        scripts.push({type: 'application/ld+json', innerHTML: jsonLD})
    }

    return (
        <Helmet
            htmlAttributes={{
                lang,
            }}
            title={metaTitle}
            titleTemplate={`%s`}
            meta={meta}
            link={links}
            script={scripts}
        >
            {/* Workaround for not working httpEquiv in meta prop*/}
            {redirectUrl ? <meta httpEquiv="refresh" content={`0; url=${redirectUrl}`} /> : null}
        </Helmet>
    )
}

SEO.defaultProps = {
    title: '',
    description: '',
    imagePath: null,
    noindex: false,
    jsonLD: '',
    redirectUrl: '',
}

SEO.propTypes = {
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    imagePath: PropTypes.string,
    noindex: PropTypes.bool,
    jsonLD: PropTypes.string,
    redirectUrl: PropTypes.string,
}

const getPathWithLanguage = (siteUrl, originalPath, language) => {
    return `${siteUrl}${joinUrl(originalPath, language)}`
}

export default SEO
